import { ActionType } from '../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { Endpoints } from '../../../core/api/endpoints';
import { AppState } from '../../../core/redux/appState';
import { getLoading, addError, noLoadingNoError, addLoading } from '../../../core/api/module';


const KEY_ACTION = ActionType.SendEQScoresRequest;
const ACTIONS    = [
  ActionType.SendEQScoresRequest,
  ActionType.SendEQScoresResponse,
  ActionType.SendEQScoresError,
];


type Payload = string;


type Meta = {
  email: string;
  projectId: number;
};


/**
 * Dispatch this to send an email inviting a newly added collaborator to view the participant's dev plan
 *
 * @param projectId - the database Id for the project the dev plan for which this person has been added
 * @param participantId - the database Id for the participant for whom this person has been added
 * @param email - the collaborator's email address
 * @param comment - additional text supplied by the user, to be included in the collaborator's invite email
 * @param languageId - the language in which the invite should be sent (usually just the project default language)
 * @param releaseId - the release id for the report which will be used to get the scores
 */
export const sendEQScoresAction = (
  projectId: number,
  participantId: number,
  email: string,
  comment: string,
  languageId: number,
  releaseId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SendEQScores,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:     projectId,
        participantId: participantId,
        comment:       comment,
        to:            [{ toAddress: email, languageId: languageId }],
        releaseId:     releaseId,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        email:     email,
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${email}-${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { email, projectId } = action.meta;

  const metaTag = `${email}-${projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  return noLoadingNoError(state, KEY_ACTION, metaTag);
};

export const sendEQScoresReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
